import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { playbookCategories } from "@/data/playbooks/categories";
import { getPlaybookById } from "@/data/playbooks/registry";
import { MockGenieConfig, WizardStepId } from "@/types/mockups/genie-setup-v2";

import { composeSystemPrompt, getConfigSummary } from "../composer";
import { knowledgeSources } from "../data";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

interface Step6ReviewProps {
  config: MockGenieConfig;
  onJumpToStep: (stepId: WizardStepId) => void;
}

export const Step6Review = ({ config, onJumpToStep }: Step6ReviewProps) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const summary = getConfigSummary(config);
  const composedPrompt = composeSystemPrompt(config);
  const category = config.job.categoryId
    ? playbookCategories.find((c) => c.id === config.job.categoryId)
    : null;

  const selectedPlaybooks = config.playbookIds
    .map((id) => getPlaybookById(id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  const selectedSources = config.knowledge.sources
    .map((id) => knowledgeSources.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  return (
    <div className="space-y-5">
      <ReviewSection
        genieIcon="clipboard-check"
        title="Job"
        onEdit={() => onJumpToStep("job")}
      >
        {category ? (
          <div>
            <p className="text-base font-medium text-gray-900">{category.name}</p>
            <p className="text-sm text-gray-600 mt-1">{category.description}</p>
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic">Not set</p>
        )}
      </ReviewSection>

      <ReviewSection
        genieIcon="eye"
        title="Personality"
        onEdit={() => onJumpToStep("personality")}
      >
        <div className="grid grid-cols-2 gap-3">
          <PersonalityCell label="Role" value={summary.role} />
          <PersonalityCell label="Length" value={summary.length} />
          <PersonalityCell label="Energy" value={summary.energy} />
          <PersonalityCell label="Approach" value={summary.approach} />
          <PersonalityCell label="Formality" value={summary.formality} />
        </div>
      </ReviewSection>

      <ReviewSection
        genieIcon="document"
        title={`Plays (${selectedPlaybooks.length})`}
        onEdit={() => onJumpToStep("playbooks")}
      >
        {selectedPlaybooks.length > 0 ? (
          <ul className="space-y-2">
            {selectedPlaybooks.map((p) => (
              <li key={p.meta.id} className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-genie-primary shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{p.meta.name}</p>
                  <p className="text-xs text-gray-500">{p.meta.tagline}</p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-400 italic">No plays selected</p>
        )}
      </ReviewSection>

      <ReviewSection
        genieIcon="database"
        title="Knowledge"
        onEdit={() => onJumpToStep("knowledge")}
      >
        {selectedSources.length > 0 ? (
          <div className="flex flex-wrap gap-2 mb-3">
            {selectedSources.map((s) => (
              <span
                key={s.id}
                className="text-xs px-2.5 py-1 rounded-full bg-genie-primary/10 text-genie-primary font-medium"
              >
                {s.label}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic mb-3">No sources connected</p>
        )}
        {config.knowledge.customContext.trim().length > 0 && (
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
              Business context
            </p>
            <p className="text-sm text-gray-700 whitespace-pre-line">
              {config.knowledge.customContext}
            </p>
          </div>
        )}
      </ReviewSection>

      {/* Advanced: composed prompt */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowPrompt((s) => !s)}
          className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <GenieIcon name="settings" size={36} className="h-9 w-9" />
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">Advanced: composed prompt</p>
              <p className="text-xs text-gray-500">
                What your choices tell the genie behind the scenes.
              </p>
            </div>
          </div>
          {showPrompt ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </button>
        {showPrompt && (
          <div className="px-5 pb-5">
            <pre className="bg-[#222B45] text-gray-100 rounded-lg p-4 text-xs font-mono whitespace-pre-wrap leading-relaxed overflow-x-auto">
              {composedPrompt || "(empty — pick options to compose your prompt)"}
            </pre>
            <p className="text-xs text-gray-400 mt-2">
              Generated automatically from your choices. You never have to write this.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const ReviewSection = ({
  genieIcon,
  title,
  onEdit,
  children,
}: {
  genieIcon: GenieIconName;
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-3">
        <GenieIcon name={genieIcon} size={36} className="h-9 w-9" />
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="text-xs font-medium text-genie-primary hover:underline"
      >
        Edit
      </button>
    </div>
    <div>{children}</div>
  </div>
);

const PersonalityCell = ({ label, value }: { label: string; value: string }) => (
  <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
    <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
    <p className="text-sm font-medium text-gray-900 mt-0.5">{value}</p>
  </div>
);
